import { AppliedCoupon, AssignedCoupon } from '@app/types/cart/Coupon';
import { Box, Card, Stack } from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import React, { FC, useMemo } from 'react';
import { CouponItem } from './CouponItem/CouponItem';
import styles from './CouponList.module.scss';
import { CouponsError } from './CouponsError/CouponsError';
import { EmptyCoupons } from './EmptyCoupons/EmptyCoupons';

export const messages = defineMessages({
  ariaLabelText: {
    defaultMessage: 'Assigned coupons ',
  },
});

interface CouponListProps {
  locale: string;
  isGetUserQueryServerError: boolean;
  appliedCoupon?: AppliedCoupon;
  assignedCoupons?: AssignedCoupon[];
  onCouponAction: (coupon: AssignedCoupon, isApply: boolean) => void;
  isLoading: boolean;
}

export const CouponList: FC<CouponListProps> = ({
  locale,
  isGetUserQueryServerError,
  appliedCoupon,
  assignedCoupons,
  onCouponAction,
  isLoading,
}) => {
  const intl = useIntl();

  const isEmpty = useMemo(
    () => !!assignedCoupons && assignedCoupons.length === 0,
    [assignedCoupons]
  );

  const renderCouponList = ({
    couponsError,
    emptyCoupons,
    normalCoupons,
  }: {
    couponsError: React.ReactNode;
    emptyCoupons: React.ReactNode;
    normalCoupons: React.ReactNode;
  }) => {
    if (isGetUserQueryServerError) {
      return couponsError;
    }
    if (isEmpty) {
      return emptyCoupons;
    }
    return normalCoupons;
  };

  return (
    // The tiny margins provide spacing for the card box shadows to appear
    <Box element="ul" className={styles.couponList} marginBottom="xxxs">
      {renderCouponList({
        couponsError: <CouponsError />,
        emptyCoupons: <EmptyCoupons />,
        normalCoupons: (
          <Stack
            spacing="m"
            role="list"
            aria-label={intl.formatMessage(messages.ariaLabelText)}
          >
            {assignedCoupons?.map((coupon, index) => (
              <Box element="li" key={`coupon_${coupon?.code}`}>
                {coupon && (
                  <Card>
                    <CouponItem
                      locale={locale}
                      coupon={coupon}
                      appliedCoupon={appliedCoupon || undefined}
                      onAction={onCouponAction}
                      loading={isLoading}
                      testId={`coupon-item-${index}`}
                    />
                  </Card>
                )}
              </Box>
            ))}
          </Stack>
        ),
      })}
    </Box>
  );
};
